// ============================================
// INSTALL APP BUTTON
// Floating button + header icon that triggers the PWA install prompt.
//
// Behavior:
//   - Captures `beforeinstallprompt` event internally (Chrome/Android/Edge)
//   - On iOS Safari (no install event), shows manual "Add to Home Screen" instructions
//   - Hides itself once the app is installed
//   - Hides if user dismisses for 14 days
//   - Works as floating button OR inline header icon (use the `variant` prop)
// ============================================

import React, { useState, useEffect, useRef } from 'react';

const DISMISS_KEY = 'install_btn_dismissed_at';
const DISMISS_COOLDOWN_DAYS = 14;

const isIOS = () => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const isAndroid = () => {
  if (typeof window === 'undefined') return false;
  return /Android/.test(navigator.userAgent);
};

const isInStandaloneMode = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const wasRecentlyDismissed = () => {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const days = (Date.now() - parseInt(v, 10)) / (1000 * 60 * 60 * 24);
    return days < DISMISS_COOLDOWN_DAYS;
  } catch {
    return false;
  }
};

// Shared deferred prompt — set by the listener in this file (NOT the PWAInstallPrompt component).
// Having a single source of truth prevents both components from trying to .prompt() the same event.
let deferredPrompt = null;
let listenerAttached = false;

const attachListener = () => {
  if (listenerAttached || typeof window === 'undefined') return;
  listenerAttached = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new CustomEvent('installable:ready'));
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    try { localStorage.removeItem(DISMISS_KEY); } catch {}
    window.dispatchEvent(new CustomEvent('installable:installed'));
  });
};

const InstallAppButton = ({ variant = 'floating' }) => {
  const [visible, setVisible] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [installed, setInstalled] = useState(isInStandaloneMode());
  const [isInstallable, setIsInstallable] = useState(!!deferredPrompt);
  const clickedRef = useRef(false);

  useEffect(() => {
    attachListener();

    // Already installed — hide forever
    if (isInStandaloneMode()) {
      setInstalled(true);
      return;
    }

    // Recently dismissed — hide for cooldown period UNLESS user clicked something
    // that dispatched the 'pwa:show-install' event manually
    if (!wasRecentlyDismissed()) {
      // On iOS (no event), show button anyway so users can get AddToHomeScreen help
      // On Android/desktop, show as soon as installable OR after 3 sec as a fallback
      if (isIOS()) {
        setTimeout(() => setVisible(true), 3000);
      } else {
        // If installable event already fired, show immediately
        if (deferredPrompt) {
          setTimeout(() => setVisible(true), 1500);
        }
        // Otherwise wait for the event or fallback timer
      }
    }

    const onInstallable = () => {
      setIsInstallable(true);
      if (!wasRecentlyDismissed() && !clickedRef.current) {
        setTimeout(() => setVisible(true), 1500);
      }
    };
    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
      setShowIOSModal(false);
    };
    const onManualShow = () => {
      setVisible(true);
    };

    window.addEventListener('installable:ready', onInstallable);
    window.addEventListener('installable:installed', onInstalled);
    window.addEventListener('pwa:show-install', onManualShow);

    return () => {
      window.removeEventListener('installable:ready', onInstallable);
      window.removeEventListener('installable:installed', onInstalled);
      window.removeEventListener('pwa:show-install', onManualShow);
    };
  }, []);

  const handleClick = async () => {
    clickedRef.current = true;

    // iOS path — browser can't trigger native install, show instructions
    if (isIOS()) {
      setShowIOSModal(true);
      return;
    }

    // Chrome/Android/Edge path — use captured event
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        deferredPrompt = null;
        if (outcome === 'accepted') {
          setInstalled(true);
          setVisible(false);
        }
      } catch (err) {
        console.warn('[InstallApp] prompt error:', err);
        showFallbackInstructions();
      }
    } else {
      // Event never fired — show Android-specific fallback
      showFallbackInstructions();
    }
  };

  const showFallbackInstructions = () => {
    // No deferred prompt — user needs to install via browser menu
    setShowIOSModal(true); // reuse modal with generic instructions
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  // Don't render if installed
  if (installed) return null;

  // ── HEADER ICON VARIANT ──
  // A small icon button to place inline with other header actions.
  if (variant === 'header') {
    if (installed) return null;
    return (
      <>
        <button
          onClick={handleClick}
          title="Install App"
          aria-label="Install App"
          style={S.headerBtn}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
          </svg>
        </button>
        {showIOSModal && <InstallInstructionsModal onClose={() => setShowIOSModal(false)} />}
      </>
    );
  }

  // ── FLOATING BUTTON VARIANT (default) ──
  if (!visible) return null;

  return (
    <>
      <button
        onClick={handleClick}
        style={S.floatingBtn}
        title="Install Glitz & Glam app"
        aria-label="Install Glitz & Glam app"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#04020a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
          <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
        </svg>
      </button>
      <button
        onClick={dismiss}
        style={S.floatingDismiss}
        title="Hide"
        aria-label="Hide install button"
      >
        ✕
      </button>
      {showIOSModal && <InstallInstructionsModal onClose={() => setShowIOSModal(false)} />}
    </>
  );
};

// ── iOS / Fallback Instructions Modal ──
const InstallInstructionsModal = ({ onClose }) => {
  const ios = isIOS();
  const android = isAndroid();
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalBox} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={S.modalClose} aria-label="Close">✕</button>
        <div style={S.modalIcon}>💎</div>
        <div style={S.modalTitle}>Install Glitz &amp; Glam</div>
        <div style={S.modalSub}>Get our app on your home screen for quick shopping</div>
        {ios ? (
          <ol style={S.modalSteps}>
            <li>Tap the <strong style={S.modalKbd}>Share</strong> button <span style={S.shareIcon}>⎋</span> in Safari</li>
            <li>Scroll and tap <strong style={S.modalKbd}>Add to Home Screen</strong></li>
            <li>Tap <strong style={S.modalKbd}>Add</strong> in the top-right</li>
          </ol>
        ) : android ? (
          <ol style={S.modalSteps}>
            <li>Tap the <strong style={S.modalKbd}>⋮</strong> menu in Chrome</li>
            <li>Tap <strong style={S.modalKbd}>Install app</strong> or <strong style={S.modalKbd}>Add to Home screen</strong></li>
            <li>Confirm to finish installing</li>
          </ol>
        ) : (
          <ol style={S.modalSteps}>
            <li>Look for the install icon <strong style={S.modalKbd}>⊕</strong> in the address bar</li>
            <li>Or click the <strong style={S.modalKbd}>⋮</strong> menu → <strong style={S.modalKbd}>Install Glitz &amp; Glam</strong></li>
            <li>Click <strong style={S.modalKbd}>Install</strong> when prompted</li>
          </ol>
        )}
        <button onClick={onClose} style={S.modalCta}>Got it</button>
      </div>
    </div>
  );
};

const S = {
  // Header icon — sized to match other iconBtns in the header
  headerBtn: {
    background: 'transparent',
    border: '1px solid rgba(201,168,76,0.3)',
    color: '#c9a84c',
    borderRadius: 100,
    width: 34,
    height: 34,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    transition: 'all 0.2s',
  },
  // Floating button — TOP of the stack (above AI Chatbot & WhatsApp)
  //   WhatsApp:   bottom 24,  size 56
  //   AI bot:     bottom 92,  size 56   (12px gap above WhatsApp)
  //   Install:    bottom 160, size 56   (12px gap above AI bot)
  floatingBtn: {
    position: 'fixed',
    bottom: 160,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #f0d080 0%, #c9a84c 60%, #9a7010 100%)',
    border: '2px solid rgba(255,250,220,0.25)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(201,168,76,0.45), 0 0 0 0 rgba(240,208,128,0.6)',
    zIndex: 9998,
    padding: 0,
    animation: 'installPulse 2.4s ease-in-out infinite',
  },
  // Tiny dismiss X — top-right of Install button
  floatingDismiss: {
    position: 'fixed',
    bottom: 160 + 40,     // 200
    right: 24 - 6,        // 18 (slightly sticks out to the right)
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#2a2440',
    border: '1px solid rgba(201,168,76,0.5)',
    color: 'rgba(232,232,245,0.75)',
    cursor: 'pointer',
    fontSize: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: 0,
    lineHeight: 1,
  },
  // Instructions modal
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(4,2,10,0.72)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    animation: 'fadeIn 0.2s ease-out',
  },
  modalBox: {
    position: 'relative',
    maxWidth: 440,
    width: '100%',
    background: 'linear-gradient(135deg, rgba(28,20,48,0.98), rgba(18,12,32,0.98))',
    border: '1px solid rgba(201,168,76,0.35)',
    borderRadius: 18,
    padding: '32px 28px 24px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    textAlign: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    animation: 'slideUpModal 0.3s ease-out',
  },
  modalClose: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: 'transparent',
    border: '1px solid rgba(201,168,76,0.2)',
    color: 'rgba(232,232,245,0.6)',
    cursor: 'pointer',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  modalIcon: {
    fontSize: 40,
    marginBottom: 8,
  },
  modalTitle: {
    fontFamily: 'Georgia, serif',
    color: '#f0d080',
    fontSize: 22,
    fontWeight: 600,
    marginBottom: 6,
  },
  modalSub: {
    color: 'rgba(232,232,245,0.65)',
    fontSize: 13,
    marginBottom: 22,
    lineHeight: 1.5,
  },
  modalSteps: {
    textAlign: 'left',
    color: 'rgba(232,232,245,0.85)',
    fontSize: 14,
    lineHeight: 1.8,
    paddingLeft: 22,
    margin: '0 0 22px 0',
  },
  modalKbd: {
    color: '#c9a84c',
    fontWeight: 600,
  },
  shareIcon: {
    display: 'inline-block',
    padding: '1px 6px',
    background: 'rgba(201,168,76,0.15)',
    border: '1px solid rgba(201,168,76,0.3)',
    borderRadius: 4,
    fontSize: 13,
    margin: '0 2px',
    color: '#c9a84c',
  },
  modalCta: {
    background: 'linear-gradient(135deg, #c9a84c, #f0d080)',
    color: '#04020a',
    border: 'none',
    borderRadius: 100,
    padding: '12px 32px',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    cursor: 'pointer',
    width: '100%',
  },
};

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('install-btn-kf')) {
  const style = document.createElement('style');
  style.id = 'install-btn-kf';
  style.textContent = `
    @keyframes installPulse {
      0%, 100% { box-shadow: 0 4px 20px rgba(201,168,76,0.45), 0 0 0 0 rgba(240,208,128,0.6); }
      50%      { box-shadow: 0 4px 20px rgba(201,168,76,0.55), 0 0 0 12px rgba(240,208,128,0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes slideUpModal {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
}

export default InstallAppButton;
